import Vue from 'vue';
import Component from 'vue-class-component';
import { Inject, Mixins, Prop} from 'vue-property-decorator';
import { Status } from '@/models/status.model';
import { CourseToolsMixin } from '@/courses/mixins/course-tools.mixin';
import { _COURSE_STORE } from '@/courses/values/courses.values';
import { _MEETINGS_STORE } from '@/meetings/values/meetings.values';
import { CourseService } from '@/courses/services/courses.service';
import { COURSES_EVENTS } from '@/courses/values/courses-events.values';
import { Section } from '@/courses/models/section.model';
import EdTeacherMeetingTransparency from '@/core/components/ed-meeting-transparency/ed-teacher-meeting-transparency/ed-teacher-meeting-transparency.component.vue';
import EdStudentMeetingTransparency from '@/core/components/ed-meeting-transparency/ed-student-meeting-transparency/ed-student-meeting-transparency.component.vue';
import { SECTION_TYPE_CODES } from '@/core/values/ed-transparency-meeting.values';
import { COURSE_EVENTS } from '@/content/values/events';
import { PRESENTATION_MODE_EVENTS } from '@/content/values/ed-presentation-mode.values';
import { ScreenMirroringMixin } from '@/screen-mirroring/mixins/screen-mirroring.mixin';


@Component({
  name: 'ed-html-viewer',
  components: {EdTeacherMeetingTransparency, EdStudentMeetingTransparency}
})
export default class EdHtmlViewer extends Mixins(CourseToolsMixin, ScreenMirroringMixin) { 
  
  /* Props */
  @Prop() readonly url: string;
  @Prop({default: null}) readonly initNumberPage: number;
  @Prop() documentIndex : number;
  @Prop() courseId : number;
  @Prop() readonly section: Section;
  
  /* Data */
  public urlHtml: string = '';
  public pdfStatus: Status = new Status();
  public isFullScreen: boolean = false;
  public isProcessing: boolean = false;
  public interval: any = null;
  public pagesLoaded: boolean = false;
  public mainIframe: any;

  @Inject()
  messageEvent: any;
  

  private processEvents = {
    showSlide: this.showSlide,
    moveSlide: this.moveSlide,
  }

  private currentData : {
    currentTime: number,
    eventType: string,
    slide: string
  } = {
    currentTime: 0,
    eventType: 'showSlide',
    slide: '0'
  }

  
  /* Computed */
  get zoomObject(): boolean {
    return this.$store.state.CourseStore.zoomObject;
  }

  get courseSelected(): number {
    return this.$store.state.CourseStore.presentationCourseSelected;
  }

  get divId() {
    return `slideFullScreen${new Date().getTime()}`;
  }

  get isPresentationTeacherMode() {
    return this.$store.state.CourseStore.isPresentationTeacherMode;
  }

  get isPresentationStudentMode() {
    return this.$store.state.CourseStore.isPresentationStudentMode;
  }

  get isPresentationMode () {
    return this.isPresentationStudentMode || this.isPresentationTeacherMode;
  }

  get isWhiteboardTransparency() {
    return this.$store.state.MeetingsStore.isWhiteboardTransparency;
  }
  
  get isSameCourse() {
    return this.courseSelected === this.section.courseId;
  }

  get pdfID() {
    return null;
  }

  get documentHash() {
    // desired length of Id
    var idStrLen = 32;
    // always start with a letter -- base 36 makes for a nice shortcut
    var idStr = (Math.floor((Math.random() * 25)) + 10).toString(36) + '_';
    // add a timestamp in milliseconds (base 36 again) as the base
    idStr += (new Date()).getTime().toString(36) + '_';
    // similar to above, complete the Id using random, alphanumeric characters
    do {
        idStr += (Math.floor((Math.random() * 35))).toString(36);
    } while (idStr.length < idStrLen);

    return (idStr);
  }
  
 get isSameUser() {
    return this.currentBoardUserId === this.userId;
  }
  
  get isTeacherScreenUser() {
    return this.$store.state.MeetingsStore.currentBoardUser.isTeacher;
  }

  get isBoardSectionHomework() {
    return this.$store.state.CourseStore.isBoardSectionHomework;
  }

  /* Methods */
  private async setPDF(url: string) {
    const hash = this.getSlideHash(url);
    // This viewer also opens files that do not belong to Media. Only Media slides need a private entry URL;
    // keep the existing behavior for every other file until its owner migrates it.
    if (!hash) {
      this.urlHtml = await CourseService.getSectionInfoByLink(url);
      this.pdfStatus.setLoad();
      return;
    }
    const access = await CourseService.getSlideAccess(hash);
    // This URL opens the slide and all of its files with the current Media access. Keep it only in the iframe state.
    this.urlHtml = access.url;
    this.pdfStatus.setLoad();
  }

  private getSlideHash(url: string): string | null {
    const sectionHash = this.section && this.section.assetsInfo && this.section.assetsInfo[0]
      ? this.section.assetsInfo[0].hash : null;
    if (typeof sectionHash === 'string' && /^[A-Fa-f0-9]{64}$/.test(sectionHash)) return sectionHash;
    const match = typeof url === 'string' && url.match(/\/([A-Fa-f0-9]{64})(?:[/?#]|$)/);
    return match ? match[1] : null;
  }

  public async refresh() {
    this.pdfStatus.setLoading();
    try {
      await this.setPDF(this.url);
    } catch(err) {
      this.pdfStatus.setError();
    }
  }

  public setViewer() {
    setTimeout(() => { this.renderPDF(); }, 100);
  }

  public async renderPDF() {
    try {
      this.pdfStatus.setLoading();
      await this.setPDF(this.url);
    } catch(err) {
      this.pdfStatus.setError();
    }
  }

  public requestFullscreen() {
    this.isFullScreen = true;

    const elem : any = document.getElementById(this.divId);
    
    if (elem.requestFullscreen) {
      elem.requestFullscreen();
    } else if (elem.webkitRequestFullscreen) { /* Safari */
      elem.webkitRequestFullscreen();
    } else if (elem.msRequestFullscreen) { /* IE11 */
      elem.msRequestFullscreen();
    }
  }

  public closeFullscreen() {
    this.isFullScreen = false;

    const docEle: any = document;

    if (docEle.exitFullscreen) {
      docEle.exitFullscreen();
    } else if (docEle.webkitExitFullscreen) { /* Safari */
      docEle.webkitExitFullscreen();
    } else if (docEle.msExitFullscreen) { /* IE11 */
      docEle.msExitFullscreen();
    }
  }

  public validateFullScreen() {
    const docEle : any = document;
    if (
      !docEle.fullscreenElement && /* Standard syntax */
      !docEle.webkitFullscreenElement && /* Safari and Opera syntax */
      !docEle.msFullscreenElement /* IE11 syntax */
    ) {
      this.isFullScreen = false;
    }
  }


  loadingPage() : void {
    if((document.querySelectorAll('.html-container')[0] as HTMLImageElement)?.offsetHeight == 0) {
      setTimeout(() => {
        this.loadingPage();
      }, 200);
    } else {
      const transparencyRef : any = this.$refs.teacherTransparency || this.$refs.studentTransparency;
      if(transparencyRef && !this.pagesLoaded) {
        let htmlElement : any = document.querySelectorAll('.html-container')[0];
        let data = { height: htmlElement.offsetHeight, width: htmlElement.offsetWidth, sectionType: SECTION_TYPE_CODES.LESSON_PPT_HTML, page: 1 };
        transparencyRef.loadingPage(data);
        this.$store.commit( _MEETINGS_STORE.MUTATIONS.UPDATE_ORIGINAL_PAGES_SIZE ,{ documentHash: this.documentHash, pageNumber: 1, data});
        this.pagesLoaded = true;
        this.$store.commit(_MEETINGS_STORE.MUTATIONS.UPDATE_LOADED_BOARDS_IN_DOM, 0);     
      }
    }
  }

  changeSlide(message: any) {
    if(message && this.isPresentationStudentMode) {
      if(this.processEvents[message.data?.eventType]) 
        this.processEvents[message.data.eventType](message.data);
    }
  }

  moveCurrentSlide(data: any) {
    if(data) {
      if(this.processEvents[data.type]) 
        this.processEvents[data.type](data.data);
    }
  }

  showSlide(data: any) {
    this.getMainIframe();
    let message = {message: 'slide', value : data.slide};
    this.sendMessageIframe(this.mainIframe, message);
  }

  moveSlide(data: any) {
    this.getMainIframe();
    let message = {message: 'move', value : data};
    this.sendMessageIframe(this.mainIframe, message);
  }

  sendCurrentData(slideNumber) {
    this.currentData.slide = slideNumber;
    this.$edEventBus.$emit(COURSE_EVENTS.CHANGE_SLIDE, 
      {
        data: this.currentData, 
        type: 'changeSlide'
      }
    );
  }

  slideMessageWithIframeByStudent() {
    window.addEventListener('message', this.messageEventsIframeByStudent, false);
  }

  slideMessageWithIframeByTeacher() {
    this.$edEventBus.$emit(PRESENTATION_MODE_EVENTS.LOAD_PRESENTATION_ELEMENTS);
    window.addEventListener('message', this.messageEventsIframeByTeacher, false);
  }

  getMainIframe() {
    this.mainIframe = document.getElementById('mainframe');
    this.mainIframe = this.mainIframe ? this.mainIframe.contentWindow : null;
  }

  messageEventsIframeByStudent(e) {
    if(e.data.message === 'connect' && e.data.value === 'READY') {
      this.getMainIframe();
      this.loadingPage();
      if(this.messageEvent?.has('Slide')) {
        this.changeSlide(JSON.parse(this.messageEvent?.get('Slide')));
      }
      this.$edEventBus.$emit(PRESENTATION_MODE_EVENTS.LOAD_PRESENTATION_ELEMENTS);
      window.removeEventListener('message', this.messageEventsIframeByStudent, false);
    }
  }

  messageEventsIframeByTeacher(e) {
    var data = e.data;

    if(data) {
      if(data.message === 'connect' && data.value === 'READY') {
        this.getMainIframe();
        this.loadingPage();
        this.$edEventBus.$on(COURSE_EVENTS.MOVE_SLIDE, this.moveCurrentSlide);
        let message = {message: 'start'};  
        this.sendMessageIframe(this.mainIframe, message);
        this.sendCurrentData('0');
      } else if(data.message === 'slide') {
        this.sendCurrentData(data.value);
      }
    }
  }

  sendMessageIframe(windowObj, message) {
    if(windowObj) {
      windowObj.postMessage(message, '*');
    }
  }


  /* Hooks */
  created() {
      if(!this.url) {
        this.isProcessing = true;
      } else {
        this.isProcessing = false;
      }
  }

  mounted() {
    setTimeout(() => { this.setViewer(); }, 1000);

    if(this.isProcessing) {
        let fileData = { sectionId: this.section.sectionId , materialId: null ,
          courseId: this.section.courseId , classId: this.section.classId};
        let isProcessing = this.checkStorage(fileData.sectionId , null);
        if(isProcessing) {
          this.interval = setInterval((data) => {
            this.waitForReadyFileInfo(this.interval , data);
          }, 15000 , fileData);
        }      
    }

    this.$edEventBus.$on(COURSES_EVENTS.SLIDES_FULL_SCREEN, this.requestFullscreen);

    /* Standard syntax */
    document.getElementById(this.divId).addEventListener('fullscreenchange', this.validateFullScreen.bind(this));

    /* Firefox */
    document.getElementById(this.divId).addEventListener('mozfullscreenchange', this.validateFullScreen.bind(this));

    /* Chrome, Safari and Opera */
    document.getElementById(this.divId).addEventListener('webkitfullscreenchange', this.validateFullScreen.bind(this));

    /* IE / Edge */
    document.getElementById(this.divId).addEventListener('msfullscreenchange', this.validateFullScreen.bind(this));

    
    if(this.isPresentationStudentMode) {
      this.slideMessageWithIframeByStudent();
      this.$edEventBus.$on(COURSE_EVENTS.CHANGE_SLIDE, this.changeSlide);
    } else if(this.isPresentationTeacherMode) {
      this.slideMessageWithIframeByTeacher();
    }
  }

  destroyed() {
    if(document.getElementById(this.divId)) {
      /* Standard syntax */
      document.getElementById(this.divId).removeEventListener('fullscreenchange', this.validateFullScreen.bind(this));
      /* Firefox */
      document.getElementById(this.divId).removeEventListener('mozfullscreenchange', this.validateFullScreen.bind(this));
      /* Chrome, Safari and Opera */
      document.getElementById(this.divId).removeEventListener('webkitfullscreenchange', this.validateFullScreen.bind(this));
      /* IE / Edge */
      document.getElementById(this.divId).removeEventListener('msfullscreenchange', this.validateFullScreen.bind(this));
    }


    if(this.isPresentationStudentMode) {
      this.$edEventBus.$off(COURSE_EVENTS.CHANGE_SLIDE, this.changeSlide);
    } else if(this.isPresentationTeacherMode) {
      window.removeEventListener('message', this.messageEventsIframeByTeacher, false);
      this.$edEventBus.$off(COURSE_EVENTS.MOVE_SLIDE, this.moveCurrentSlide);
    }
  }
  
}
